import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import styles from './BottomSheet.module.css';
const DRAG_SLOP_PX = 8;
const FLICK_VELOCITY_PX_PER_MS = 0.5;
export function BottomSheet({ onClose, zIndex, scrollable = true, children }) {
    const overlayRef = useRef(null);
    const sheetRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    // Hebt das Sheet über die Bildschirmtastatur: der sichtbare Viewport (visualViewport)
    // schrumpft bei offener Tastatur, position:fixed-Elemente aber nicht (v. a. iOS Safari).
    useEffect(() => {
        const overlay = overlayRef.current;
        const vv = window.visualViewport;
        if (!overlay || !vv)
            return;
        const update = () => {
            const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
            overlay.style.bottom = `${inset}px`;
        };
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
        };
    }, []);
    // Beim Schließen des Sheets den Fokus lösen, damit die Bildschirmtastatur zugeht
    useEffect(() => {
        return () => {
            const active = document.activeElement;
            if (active instanceof HTMLElement && sheetRef.current?.contains(active)) {
                active.blur();
            }
        };
    }, []);
    useEffect(() => {
        const sheet = sheetRef.current;
        if (!sheet)
            return;
        let startY = 0;
        let startTime = 0;
        let delta = 0;
        let dragging = false;
        let startedAtTop = false;
        let closing = false;
        const onTouchStart = (event) => {
            const touch = event.touches[0];
            if (closing || !touch)
                return;
            startY = touch.clientY;
            startTime = event.timeStamp;
            startedAtTop = sheet.scrollTop <= 0;
            dragging = false;
            delta = 0;
        };
        const onTouchMove = (event) => {
            const touch = event.touches[0];
            if (closing || !touch)
                return;
            const dy = touch.clientY - startY;
            if (!dragging) {
                if (!startedAtTop || dy <= DRAG_SLOP_PX)
                    return;
                dragging = true;
            }
            // Vom Browser-Scroll übernehmen, solange der Sheet-Inhalt oben steht
            event.preventDefault();
            delta = Math.max(0, dy);
            sheet.style.transition = 'none';
            sheet.style.transform = `translateY(${delta}px)`;
        };
        const settleBack = () => {
            sheet.style.transition = 'transform 0.22s cubic-bezier(0.22, 0.9, 0.3, 1)';
            sheet.style.transform = '';
        };
        const onTouchEnd = (event) => {
            if (!dragging || closing)
                return;
            dragging = false;
            const velocity = delta / Math.max(1, event.timeStamp - startTime);
            const shouldClose = delta > sheet.offsetHeight / 3 || velocity > FLICK_VELOCITY_PX_PER_MS;
            if (shouldClose) {
                closing = true;
                sheet.style.transition = 'transform 0.2s ease-in';
                sheet.style.transform = 'translateY(100%)';
                window.setTimeout(() => onCloseRef.current(), 200);
            }
            else {
                settleBack();
            }
        };
        const onTouchCancel = () => {
            if (!dragging || closing)
                return;
            dragging = false;
            settleBack();
        };
        sheet.addEventListener('touchstart', onTouchStart, { passive: true });
        // passive: false, damit preventDefault() das native Scrollen stoppen darf
        sheet.addEventListener('touchmove', onTouchMove, { passive: false });
        sheet.addEventListener('touchend', onTouchEnd);
        sheet.addEventListener('touchcancel', onTouchCancel);
        return () => {
            sheet.removeEventListener('touchstart', onTouchStart);
            sheet.removeEventListener('touchmove', onTouchMove);
            sheet.removeEventListener('touchend', onTouchEnd);
            sheet.removeEventListener('touchcancel', onTouchCancel);
        };
    }, []);
    return (_jsxs("div", { ref: overlayRef, className: styles.overlay, style: { zIndex }, children: [_jsx("div", { className: styles.backdrop, onClick: onClose }), _jsxs("div", { ref: sheetRef, className: scrollable ? styles.sheetScrollable : styles.sheet, children: [_jsx("div", { className: styles.handle }), children] })] }));
}
